import { Button, Group, Paper, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck, IconPlugConnected } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';

/**
 * One line that answers "is it working?".
 *
 * Today that question costs three tab-opens and three different card layouts to
 * answer, which means in practice nobody asks it until a customer complains.
 * Assembled entirely client-side from data already on the page.
 */
export interface HealthSummaryProps {
  connectedCount: number;
  attentionCount: number;
  /** Set when at least one channel is proven to be receiving. */
  lastMessageAt?: Date | null;
}

function relative(d: Date): string {
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export function HealthSummary({
  connectedCount,
  attentionCount,
  lastMessageAt,
}: HealthSummaryProps) {
  if (connectedCount === 0) {
    return (
      <Paper withBorder radius="md" p="sm">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon size={28} radius="xl" variant="light" color="gray">
              <IconPlugConnected size={16} />
            </ThemeIcon>
            <Text size="sm">Nothing connected yet.</Text>
          </Group>
          <Button size="compact-sm" component={Link} to="/vendor/setup" variant="light">
            Start setup
          </Button>
        </Group>
      </Paper>
    );
  }

  const bad = attentionCount > 0;

  return (
    <Paper withBorder radius="md" p="sm" bg={bad ? 'var(--mantine-color-orange-light)' : undefined}>
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon size={28} radius="xl" variant="light" color={bad ? 'orange' : 'green'}>
          {bad ? <IconAlertTriangle size={16} /> : <IconCircleCheck size={16} />}
        </ThemeIcon>
        <Text size="sm">
          {bad
            ? `${attentionCount} connection${attentionCount === 1 ? '' : 's'} need${attentionCount === 1 ? 's' : ''} attention.`
            : 'All connections working.'}
          {lastMessageAt ? ` Last message ${relative(lastMessageAt)}.` : ''}
        </Text>
      </Group>
    </Paper>
  );
}
